import { useRef, useState, useEffect } from "react";
import { useFrame } from "@react-three/fiber";
import { Html } from "@react-three/drei";
import * as THREE from "three";
import {
  nodeHeight,
  nodeWidth,
  emissiveBase,
  flickerAmount,
  heatColor,
  isHighPrestige,
} from "../lib/scoring";
import { monogram } from "../components/Seal";
import { readableText } from "../lib/colors";

const GOLD = "#D4AF37";
const BASE = "#EEF1F5";
const COMPARE_COLORS = ["#D4AF37", "#0F172A"];
const LOD_FAR = 15; // world units: beyond this a node draws as a simple sphere
const _tmp = new THREE.Vector3();

export function InstitutionNode({
  institution,
  pos,
  isActive,
  onHover,
  onSelect,
  category = "composite",
  range,
  compareIndex = -1,
  bloom = false,
  showSeal = false,
  visible = true,
  pulseKey = 0,
}) {
  const meshRef = useRef();
  const capRef = useRef();
  const groupRef = useRef();
  const pulseStart = useRef(0);
  const [hovered, setHovered] = useState(false);
  const [far, setFar] = useState(false);

  useEffect(() => {
    if (pulseKey) pulseStart.current = performance.now();
  }, [pulseKey]);

  const [x, z] = pos;
  const h = nodeHeight(institution);
  const w = nodeWidth(institution);
  const eBase = emissiveBase(institution);
  const flick = flickerAmount(institution);
  const capY = h + 0.08 + w * 0.55;

  const nodeColor = institution.color || GOLD;

  // Heatmap recoloring
  const heatOn = category !== "composite";
  const catVal = heatOn ? institution[category] : null;
  let capColor = nodeColor;
  let pillarEmissive = nodeColor;
  if (heatOn) {
    if (catVal == null) {
      capColor = "#CBD5E1";
      pillarEmissive = "#CBD5E1";
    } else {
      const t = range ? (catVal - range.min) / (range.max - range.min || 1) : 0.5;
      capColor = heatColor(t);
      pillarEmissive = capColor;
    }
  }

  const showHalo = bloom && isHighPrestige(institution);

  useFrame((state) => {
    const t = state.clock.elapsedTime;
    // Level-of-Detail: distant nodes collapse to a single low-poly sphere (no
    // DOM label) to keep the frame rate smooth with 100 institutions on screen.
    const dist = state.camera.position.distanceTo(_tmp.set(x, h * 0.5, z));
    const nf = dist > LOD_FAR && !hovered && !isActive;
    if (nf !== far) setFar(nf);
    if (nf) return;

    const flicker = flick > 0.02 ? Math.sin(t * 9 + x) * 0.45 * flick : 0;
    const boost = hovered || isActive ? 0.9 : 0;
    let pulseExtra = 0;
    if (pulseStart.current) {
      const el = performance.now() - pulseStart.current;
      if (el < 1800) pulseExtra = (1 - el / 1800) * 1.7;
      else pulseStart.current = 0;
    }
    const intensity = Math.max(0.1, eBase + flicker + boost + pulseExtra);
    if (meshRef.current)
      meshRef.current.material.emissiveIntensity = intensity * 0.35;
    if (capRef.current) capRef.current.material.emissiveIntensity = intensity;

    const targetScale = (hovered || isActive ? 1.12 : 1) + pulseExtra * 0.06;
    if (groupRef.current) {
      const s = groupRef.current.scale;
      const ns = s.x + (targetScale - s.x) * 0.15;
      groupRef.current.scale.set(ns, ns, ns);
    }
  });

  const active = hovered || isActive || compareIndex >= 0;

  return (
    <group
      ref={groupRef}
      visible={visible}
      position={[x, 0, z]}
      onPointerOver={(e) => {
        e.stopPropagation();
        setHovered(true);
        onHover(institution);
        document.body.style.cursor = "pointer";
      }}
      onPointerOut={() => {
        setHovered(false);
        onHover(null);
        document.body.style.cursor = "auto";
      }}
      onClick={(e) => {
        e.stopPropagation();
        onSelect(institution);
      }}
    >
      {far ? (
        /* Simple LOD sphere for distant nodes (opaque, low-poly) */
        <mesh position={[0, h * 0.5 + 0.1, 0]}>
          <sphereGeometry args={[Math.max(0.42, w * 0.7), 12, 10]} />
          <meshStandardMaterial
            color={heatOn ? capColor : nodeColor}
            emissive={heatOn ? capColor : nodeColor}
            emissiveIntensity={0.55}
            roughness={0.3}
            metalness={0.4}
          />
        </mesh>
      ) : (
        <>
          {/* Pedestal */}
          <mesh position={[0, 0.04, 0]} castShadow>
            <cylinderGeometry args={[w * 0.75, w * 0.85, 0.08, 24]} />
            <meshStandardMaterial color="#DADFE6" roughness={0.6} metalness={0.1} />
          </mesh>

          {/* Compare selection ring */}
          {compareIndex >= 0 && (
            <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0.1, 0]}>
              <torusGeometry args={[w * 1.15, 0.055, 8, 32]} />
              <meshStandardMaterial
                color={COMPARE_COLORS[compareIndex] || GOLD}
                emissive={COMPARE_COLORS[compareIndex] || GOLD}
                emissiveIntensity={0.5}
                metalness={0.5}
                roughness={0.3}
              />
            </mesh>
          )}

          {/* Architectural pillar */}
          <mesh ref={meshRef} position={[0, h / 2 + 0.08, 0]} castShadow>
            <boxGeometry args={[w, h, w]} />
            <meshStandardMaterial
              color={BASE}
              emissive={pillarEmissive}
              emissiveIntensity={eBase * 0.35}
              roughness={0.35}
              metalness={0.35}
            />
          </mesh>

          {/* Cinematic bloom halo (highest-prestige nodes) */}
          <mesh position={[0, capY, 0]} rotation={[-Math.PI / 2, 0, 0]} visible={showHalo}>
            <torusGeometry args={[w * 1.7, 0.08, 8, 28]} />
            <meshStandardMaterial
              color={nodeColor}
              emissive={nodeColor}
              emissiveIntensity={0.9}
              metalness={0.5}
              roughness={0.3}
            />
          </mesh>

          {/* Glowing obelisk cap (Prestige + Brand brilliance) */}
          <mesh ref={capRef} position={[0, capY, 0]}>
            <coneGeometry args={[w * 0.6, w * 1.1, 4]} />
            <meshStandardMaterial
              color={capColor}
              emissive={capColor}
              emissiveIntensity={eBase}
              roughness={0.2}
              metalness={0.6}
            />
          </mesh>

          {(active || showSeal) && (
            <Html
              position={[0, capY + w * 1.05, 0]}
              center
              distanceFactor={12}
              zIndexRange={[40, 0]}
            >
              <div className="node-badge">
                <div
                  className={`seal seal-sm${institution.seal_image ? " seal-photo" : ""}`}
                  data-testid={`node-seal-${institution.rank}`}
                  style={
                    institution.seal_image
                      ? { borderColor: nodeColor }
                      : { background: nodeColor, color: readableText(nodeColor) }
                  }
                >
                  {institution.seal_image ? (
                    <img src={institution.seal_image} alt="" />
                  ) : (
                    monogram(institution)
                  )}
                </div>
                {active && (
                  <div
                    className="node-label"
                    data-testid={`node-label-${institution.rank}`}
                  >
                    <span className="node-label-rank">#{institution.rank}</span>
                    {institution.short}
                  </div>
                )}
              </div>
            </Html>
          )}
        </>
      )}
    </group>
  );
}
